import { useEffect, useState, useRef } from "react";
import { MagicWand, Lightning, CaretDown, CaretRight, CheckCircle, Question, ArrowsClockwise, Prohibit, ArrowBendUpRight, Plus, Sparkle } from "@phosphor-icons/react";
import { matchReviewGenerate, matchReviewJob, matchReviewList, matchReviewUpdate, matchReviewApply, matchReviewResolveConflict, adminCollegesLiteSearch, masterRawNameClusters, masterEnrichDetails } from "../lib/api";
import { toast } from "sonner";

const STATUS_STYLE = {
  matched: "bg-emerald-50 text-emerald-700 border-emerald-200",
  unmatched: "bg-amber-50 text-amber-700 border-amber-200",
  applied: "bg-sky-50 text-sky-700 border-sky-200",
  conflict: "bg-rose-50 text-rose-700 border-rose-200",
};

function ConflictReassign({ it, onChanged }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);
  const deb = useRef(null);

  const search = (val) => {
    setQ(val); setPick(null);
    if (deb.current) clearTimeout(deb.current);
    if (val.trim().length < 2) { setResults([]); return; }
    deb.current = setTimeout(async () => {
      try {
        const res = await adminCollegesLiteSearch(val.trim(), it.state);
        setResults(res.data?.results || []);
      } catch { setResults([]); }
    }, 300);
  };

  const reassign = async () => {
    if (!pick) return;
    if (!window.confirm(`Move existing MCC ${it.conflict_existing_mcc} → ${pick.college_code} (${pick.primary_name}), then stamp ${it.raw_code} on ${it.chosen_code} and re-derive its MCC cutoffs?`)) return;
    setBusy(true);
    try {
      const { data } = await matchReviewResolveConflict(it.id, { mode: "reassign", reassign_to_code: pick.college_code });
      toast.success(`Reassigned ${data.moved_code}→${data.dest_code}, stamped ${data.new_bracket}→${data.source_code} (rebuilt ${data.mcc_summary_rows_rebuilt} MCC rows)`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reassign failed");
    } finally { setBusy(false); }
  };

  const isNamed = !/^\d{6}$/.test(String(it.conflict_existing_mcc || ""));
  const stampInPlace = async () => {
    if (!window.confirm(`Same college: keep named code ${it.conflict_existing_mcc} as codes.MCC_allot and add 6-digit ${it.raw_code} on ${it.chosen_code}? (no cutoff rederive)`)) return;
    setBusy(true);
    try {
      const { data } = await matchReviewResolveConflict(it.id, { mode: "stamp_in_place" });
      toast.success(`Stamped ${data.new_bracket} on ${data.source_code}, kept ${data.named_code} as MCC_allot`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stamp failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-1.5 pl-1 flex flex-wrap items-center gap-2" data-testid={`reassign-${it.raw_code}`}>
      {isNamed && (
        <button onClick={stampInPlace} disabled={busy} data-testid={`stamp-inplace-${it.raw_code}`}
          className="inline-flex items-center gap-1 h-6 px-2 rounded bg-emerald-600 text-white text-[11px] font-bold hover:bg-emerald-700 disabled:opacity-40">
          <CheckCircle size={12} weight="bold" /> Stamp here (keep {it.conflict_existing_mcc} as MCC_allot)
        </button>
      )}
      <span className="text-[10px] text-zinc-500 shrink-0">or reassign <code className="font-mono">{it.conflict_existing_mcc}</code> to:</span>
      <div className="relative">
        <input
          data-testid={`reassign-search-${it.raw_code}`}
          value={pick ? `${pick.college_code} · ${pick.primary_name}` : q}
          onChange={(e) => search(e.target.value)} disabled={busy}
          placeholder="search master by name/code…"
          className="w-72 h-6 px-1.5 rounded border border-zinc-300 text-[11px]" />
        {!pick && results.length > 0 && (
          <div className="absolute z-20 mt-0.5 w-96 max-h-56 overflow-auto bg-white border border-zinc-200 rounded-md shadow-lg" data-testid={`reassign-results-${it.raw_code}`}>
            {results.map((r) => (
              <button key={r.college_code} onClick={() => { setPick(r); setResults([]); }}
                data-testid={`reassign-opt-${r.college_code}`}
                className="block w-full text-left px-2 py-1 text-[11px] hover:bg-[#6043EF]/10 border-b border-zinc-50">
                <code className="font-mono text-[#6043EF]">{r.college_code}</code> · {r.primary_name}
                <span className="text-zinc-400"> · {r.city || r.state}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <button onClick={reassign} disabled={busy || !pick} data-testid={`reassign-save-${it.raw_code}`}
        className="inline-flex items-center gap-1 h-6 px-2 rounded bg-rose-600 text-white text-[11px] font-bold hover:bg-rose-700 disabled:opacity-40">
        <ArrowBendUpRight size={12} weight="bold" /> Reassign &amp; Save
      </button>
    </div>
  );
}

function ConflictSplitNew({ it, onChanged }) {
  const [open, setOpen] = useState(false);
  const [clusters, setClusters] = useState([]);
  const [checked, setChecked] = useState({});
  const [newCode, setNewCode] = useState(it.conflict_existing_mcc || "");
  const [newName, setNewName] = useState("");
  const [location, setLocation] = useState("");
  const [state, setState] = useState(it.state || "");
  const [busy, setBusy] = useState(false);

  const expand = async () => {
    const next = !open;
    setOpen(next);
    if (next && clusters.length === 0) {
      try {
        const { data } = await masterRawNameClusters(it.chosen_code, "MCC");
        setClusters(data.clusters || []);
      } catch { toast.error("Could not load raw-name clusters"); }
    }
  };

  const enrichNow = async () => {
    if (!newCode.trim()) return;
    setBusy(true);
    try {
      await masterEnrichDetails(newCode.trim());
      toast.success(`Enrichment queued for ${newCode.trim()} (Places → metadata → media)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enrich failed");
    } finally { setBusy(false); }
  };

  const submit = async () => {
    const match_names = clusters.filter((_, i) => checked[i]).map((c) => c.name);
    if (!newCode || !newName || match_names.length === 0) {
      toast.error("Pick cluster(s) to move + fill new code & name"); return;
    }
    if (!window.confirm(`Create NEW master ${newCode} ("${newName}"), move ${match_names.length} raw cluster(s) off ${it.chosen_code}, then stamp ${it.raw_code} back on ${it.chosen_code}? Re-derives BOTH.`)) return;
    setBusy(true);
    try {
      const { data } = await matchReviewResolveConflict(it.id, {
        mode: "split_new", new_code: newCode.trim(), new_name: newName.trim(),
        location: location.trim() || null, state: state.trim() || null, match_names });
      toast.success(`Split → ${data.new_code} (moved ${data.raw_rows_moved} rows, ${data.aliases_moved} aliases); rebuilt ${data.mcc_summary_rows_rebuilt} MCC rows`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Split failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-1.5 pl-1" data-testid={`splitnew-${it.raw_code}`}>
      <button onClick={expand} disabled={busy} data-testid={`splitnew-toggle-${it.raw_code}`}
        className="inline-flex items-center gap-1 h-6 px-2 rounded border border-indigo-300 text-indigo-600 text-[11px] font-bold hover:bg-indigo-50 disabled:opacity-40">
        <Plus size={12} weight="bold" /> {open ? "Hide" : "De-link / create new college"}
      </button>
      {open && (
        <div className="mt-1.5 p-2 rounded-md border border-indigo-200 bg-indigo-50/40 space-y-2">
          <div className="text-[10px] font-bold text-indigo-700 uppercase">Move these raw clusters off {it.chosen_code}:</div>
          <div className="space-y-0.5">
            {clusters.length === 0 && <div className="text-[10px] text-zinc-400">loading clusters…</div>}
            {clusters.map((c, i) => (
              <label key={i} className="flex items-start gap-1.5 text-[11px] cursor-pointer" data-testid={`splitnew-cluster-${it.raw_code}-${i}`}>
                <input type="checkbox" checked={!!checked[i]} className="mt-0.5"
                  onChange={(e) => setChecked((p) => ({ ...p, [i]: e.target.checked }))} />
                <span className="font-mono text-zinc-500 shrink-0">{c.count}</span>
                <span className="text-zinc-700 break-all">{c.name}</span>
              </label>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <input value={newCode} onChange={(e) => setNewCode(e.target.value)} placeholder="new college_code (e.g. 200344)"
              data-testid={`splitnew-code-${it.raw_code}`} className="h-6 px-1.5 rounded border border-zinc-300 font-mono text-[11px]" />
            <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="location (e.g. Manipal)"
              data-testid={`splitnew-location-${it.raw_code}`} className="h-6 px-1.5 rounded border border-zinc-300 text-[11px]" />
            <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="new primary_name"
              data-testid={`splitnew-name-${it.raw_code}`} className="col-span-2 h-6 px-1.5 rounded border border-zinc-300 text-[11px]" />
            <input value={state} onChange={(e) => setState(e.target.value)} placeholder="state"
              data-testid={`splitnew-state-${it.raw_code}`} className="h-6 px-1.5 rounded border border-zinc-300 text-[11px]" />
            <button onClick={submit} disabled={busy} data-testid={`splitnew-submit-${it.raw_code}`}
              className="inline-flex items-center justify-center gap-1 h-6 px-2 rounded bg-indigo-600 text-white text-[11px] font-bold hover:bg-indigo-700 disabled:opacity-40">
              <Plus size={12} weight="bold" /> Create New &amp; Split
            </button>
          </div>
          <div className="flex items-center gap-2 pt-0.5">
            <span className="text-[10px] text-zinc-500">Split auto-enriches the new college. Re-run manually:</span>
            <button onClick={enrichNow} disabled={busy || !newCode.trim()} data-testid={`splitnew-enrich-${it.raw_code}`}
              className="inline-flex items-center gap-1 h-6 px-2 rounded border border-amber-300 text-amber-700 text-[11px] font-bold hover:bg-amber-50 disabled:opacity-40">
              <Sparkle size={12} weight="fill" /> Enrich now
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ it, onChanged }) {
  const [code, setCode] = useState(it.chosen_code || "");
  const [busy, setBusy] = useState(false);
  const applied = it.status === "applied";

  const save = async (nextStatus) => {
    setBusy(true);
    try {
      await matchReviewUpdate(it.id, { chosen_code: code || null, status: nextStatus });
      toast.success("Saved");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="py-2 border-b border-zinc-100 text-[11px]" data-testid={`review-row-${it.raw_code}`}>
      {/* line 1 — status + code + FULL raw name */}
      <div className="flex items-start gap-2">
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded border font-bold uppercase shrink-0 ${STATUS_STYLE[it.status] || ""}`}>
          {it.status === "unmatched" ? <Question size={11} className="mr-0.5" /> : it.status === "conflict" ? <Prohibit size={11} className="mr-0.5" /> : <CheckCircle size={11} className="mr-0.5" />}
          {it.status}
        </span>
        <code className="font-mono text-zinc-500 shrink-0">{it.raw_code}</code>
        <span className="text-zinc-800 break-words" data-testid={`review-rawname-${it.raw_code}`}>{it.raw_name}</span>
      </div>
      {/* line 2 — matched master (FULL name) + controls */}
      <div className="flex flex-wrap items-center gap-2 mt-1 pl-1">
        <span className="text-zinc-400 shrink-0">→ matched:</span>
        <span className="text-zinc-700 font-medium break-words" data-testid={`review-matchname-${it.raw_code}`}>
          {it.suggested_name || <em className="text-zinc-400">(no master name)</em>}
        </span>
        <span className="text-zinc-400 shrink-0">· {it.method}{it.score != null ? ` · ${it.score}` : ""}</span>
        {it.status === "conflict" && (
          <span className="text-rose-600 font-bold shrink-0" data-testid={`review-conflict-${it.raw_code}`}>
            ⚠ master already has MCC {it.conflict_existing_mcc} — remove/reassign it first
          </span>
        )}
        <span className="flex items-center gap-2 ml-auto">
          <input
            data-testid={`review-code-${it.raw_code}`}
            value={code} onChange={(e) => setCode(e.target.value)} disabled={applied || busy}
            placeholder="college_code"
            className="w-32 h-6 px-1.5 rounded border border-zinc-300 font-mono text-[11px] disabled:bg-zinc-100" />
          {!applied && (
            <>
              <button onClick={() => save("matched")} disabled={busy || !code} data-testid={`review-save-${it.raw_code}`}
                className="h-6 px-2 rounded border border-zinc-300 font-bold text-zinc-700 hover:border-[#6043EF] hover:text-[#6043EF] disabled:opacity-40">Save</button>
              <button onClick={() => save("unmatched")} disabled={busy} title="Reject / leave unmatched"
                className="h-6 px-1.5 rounded border border-zinc-300 text-zinc-500 hover:border-rose-400 hover:text-rose-500"><Prohibit size={12} /></button>
            </>
          )}
        </span>
      </div>
      {it.status === "conflict" && <ConflictReassign it={it} onChanged={onChanged} />}
      {it.status === "conflict" && <ConflictSplitNew it={it} onChanged={onChanged} />}
    </div>
  );
}

export default function MatchReviewPanel() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [runLlm, setRunLlm] = useState(true);
  const [gen, setGen] = useState(null);   // job status
  const [applying, setApplying] = useState(false);
  const poll = useRef(null);

  const load = async () => {
    try {
      const { data } = await matchReviewList("mcc_sm");
      setItems(data.items || []);
      setSummary(data.summary || {});
    } catch { /* admin only */ }
  };
  useEffect(() => { load(); return () => clearInterval(poll.current); }, []);

  const generate = async () => {
    try {
      const { data } = await matchReviewGenerate({ source: "mcc_sm", run_llm: runLlm });
      setGen({ status: "queued" });
      toast.info("Generating review (parsing PDFs + matching)…");
      poll.current = setInterval(async () => {
        try {
          const { data: j } = await matchReviewJob(data.job_id);
          setGen(j);
          if (j.status === "completed" || j.status === "error") {
            clearInterval(poll.current);
            if (j.status === "error") toast.error(`Generate failed: ${j.error}`);
            else { toast.success("Review generated"); load(); }
          }
        } catch { clearInterval(poll.current); }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generate failed");
    }
  };

  const applyAll = async () => {
    if (!window.confirm("Apply all MATCHED rows? This registers each MCC code + name into key_registry (idempotent).")) return;
    setApplying(true);
    try {
      const { data } = await matchReviewApply({ source: "mcc_sm" });
      toast.success(`Applied ${data.applied} · collisions ${data.collisions?.length || 0}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally { setApplying(false); }
  };

  // group by state
  const groups = {};
  for (const it of items) (groups[it.state || "(no state)"] ||= []).push(it);

  return (
    <div className="border-2 border-zinc-200 rounded-xl overflow-hidden bg-white mb-5" data-testid="match-review-panel">
      <div className="px-3 py-2 flex items-center gap-3 border-b border-zinc-100 bg-zinc-50 flex-wrap">
        <button onClick={() => setOpen((o) => !o)} data-testid="match-review-toggle"
          className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#09090B] hover:text-[#6043EF]">
          {open ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
          <MagicWand size={14} weight="bold" className="text-[#6043EF]" /> College Match Review
        </button>
        <span className="text-[11px] font-mono text-zinc-500" data-testid="match-review-counts">
          {summary.matched || 0} matched · {summary.unmatched || 0} unmatched · {summary.conflict || 0} conflict · {summary.applied || 0} applied
        </span>
        <span className="text-[10px] text-zinc-400 ml-auto hidden sm:block">MCC seat matrix · apply → key_registry O(1) next run</span>
      </div>
      {open && (
        <div className="p-3" data-testid="match-review-body">
          <div className="flex items-center gap-3 flex-wrap mb-3">
            <label className="inline-flex items-center gap-1.5 text-[11px] font-bold text-zinc-600">
              <input type="checkbox" checked={runLlm} onChange={(e) => setRunLlm(e.target.checked)} data-testid="match-review-llm-toggle" />
              use LLM (verify + state-wise puzzle)
            </label>
            <button onClick={generate} disabled={gen && !["completed", "error"].includes(gen.status)} data-testid="match-review-generate"
              className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-zinc-300 text-xs font-bold text-zinc-700 hover:border-[#6043EF] hover:text-[#6043EF] disabled:opacity-50">
              <ArrowsClockwise size={13} weight="bold" /> Generate MCC SM Review
            </button>
            <button onClick={applyAll} disabled={applying || !(summary.matched > 0)} data-testid="match-review-apply-all"
              className="inline-flex items-center gap-1 h-8 px-3 rounded-md bg-[#6043EF] text-white text-xs font-bold hover:bg-[#4f36c9] disabled:opacity-50">
              <Lightning size={13} weight="fill" /> Apply All Matched
            </button>
            {gen && !["completed", "error"].includes(gen.status) && (
              <span className="text-[11px] font-mono text-amber-600" data-testid="match-review-job-status">
                {gen.status}… {gen.files_done != null ? `files ${gen.files_done}/5` : ""} {gen.unmatched != null ? `· unmatched ${gen.unmatched}` : ""}
              </span>
            )}
          </div>

          {items.length === 0 && <div className="text-xs text-zinc-400">No review items. Click “Generate MCC SM Review”.</div>}
          <div className="space-y-3">
            {Object.keys(groups).sort().map((st) => (
              <div key={st} data-testid={`review-group-${st}`}>
                <div className="text-[11px] font-bold text-[#6043EF] uppercase tracking-wide mb-0.5">{st} ({groups[st].length})</div>
                {groups[st].map((it) => <Row key={it.id} it={it} onChanged={load} />)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
